import { useState, useCallback, useRef, useEffect } from "react";
import styled from "@emotion/styled/macro";

import { Modal } from "../common/modal";
import { Text } from "../ui/text";
import { Button } from "../ui/button";
import { ButtonVariant } from "../styles/mixins/button";
import { Color, FontSize } from "../styles/enums";

/**
 * ベーステキストとルビを1文字ずつペアリングして分割する
 */
const splitRubyPairs = (
  baseText: string,
  rubyText: string,
): Array<{ id: string; base: string; ruby: string }> => {
  const baseChars = Array.from(baseText);
  const rubyChars = Array.from(rubyText);
  const baseLength = baseChars.length;
  const rubyLength = rubyChars.length;

  const charsPerBase = Math.ceil(rubyLength / baseLength);

  return baseChars.map((baseChar, idx) => {
    const startIndex = idx * charsPerBase;
    const endIndex = Math.min(startIndex + charsPerBase, rubyLength);
    const rubyPart = rubyChars.slice(startIndex, endIndex).join("");
    return {
      id: `${idx}-${baseChar}-${rubyPart}`,
      base: baseChar,
      ruby: rubyPart,
    };
  });
};

const ContentWrapper = styled.div`
  padding: 8px;
`;

const Title = styled(Text)`
  font-size: ${FontSize.SIZE_18};
  font-weight: 600;
  text-align: center;
  margin-bottom: 20px;
`;

const InputSection = styled.div`
  margin: 12px 0;
`;

const Label = styled.label`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TextField = styled.input`
  display: block;
  border: 1px solid ${Color.ACCENT_100};
  background-color: ${Color.WHITE};
  padding: 12px 16px;
  width: 100%;
  border-radius: 4px;
  font-size: ${FontSize.SIZE_14};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${Color.TINT};
  }
`;

const PreviewSection = styled.div`
  background-color: ${Color.STRONG_BG};
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const PreviewLabel = styled(Text)`
  margin-bottom: 8px;
`;

const RubyPreviewWrapper = styled.div`
  max-width: 100%;
  word-break: break-all;
  overflow-wrap: anywhere;
  text-align: center;
`;

const RubyPreview = styled.ruby`
  font-size: ${FontSize.SIZE_20};
  ruby-align: center;

  rt {
    font-size: ${FontSize.SIZE_11};
    color: ${Color.ACCENT_700};
  }

  rp {
    display: none;
  }
`;

const PlaceholderText = styled(Text)`
  color: ${Color.ACCENT_500};
`;

const TEXT_MAX_LENGTH = 20;
const RUBY_MAX_LENGTH = 50;
const FOCUS_DELAY_MS = 100;

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (text: string, ruby: string) => void;
};

export const RubyInputModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [text, setText] = useState("");
  const [ruby, setRuby] = useState("");
  const textInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && textInputRef.current) {
      setTimeout(() => {
        textInputRef.current?.focus();
      }, FOCUS_DELAY_MS);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setText("");
      setRuby("");
    }
  }, [open]);

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value.length <= TEXT_MAX_LENGTH) {
        setText(value);
      }
    },
    [],
  );

  const handleRubyChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value.length <= RUBY_MAX_LENGTH) {
        setRuby(value);
      }
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (text && ruby) {
      onSubmit(text, ruby);
      setText("");
      setRuby("");
    }
  }, [text, ruby, onSubmit]);

  const isSubmitDisabled = !text || !ruby;

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      width="320px"
      height="auto"
      close={onClose}
      overlayClose={false}
      showCloseIconInSmall
    >
      <ContentWrapper>
        <Title>ルビを挿入する</Title>

        <InputSection>
          <Label>
            <Text
              as="span"
              fz={FontSize.SIZE_12}
              fw="bold"
              color={Color.ACCENT_500}
            >
              ルビを振る文字
            </Text>
            <Text as="span" fz={FontSize.SIZE_12} color={Color.ACCENT_500}>
              {text.length}/{TEXT_MAX_LENGTH}
            </Text>
          </Label>
          <TextField
            ref={textInputRef}
            type="text"
            value={text}
            onChange={handleTextChange}
            placeholder="ルビを振る文字"
          />
        </InputSection>

        <InputSection>
          <Label>
            <Text
              as="span"
              fz={FontSize.SIZE_12}
              fw="bold"
              color={Color.ACCENT_500}
            >
              ルビ
            </Text>
            <Text as="span" fz={FontSize.SIZE_12} color={Color.ACCENT_500}>
              {ruby.length}/{RUBY_MAX_LENGTH}
            </Text>
          </Label>
          <TextField
            type="text"
            value={ruby}
            onChange={handleRubyChange}
            placeholder="ルビ"
          />
        </InputSection>

        <PreviewLabel
          as="span"
          fz={FontSize.SIZE_12}
          fw="bold"
          color={Color.ACCENT_500}
        >
          プレビュー
        </PreviewLabel>
        <PreviewSection>
          {text && ruby ? (
            <RubyPreviewWrapper>
              {splitRubyPairs(text, ruby).map((pair) => (
                <RubyPreview key={pair.id}>
                  {pair.base}
                  <rp>（</rp>
                  <rt>{pair.ruby}</rt>
                  <rp>）</rp>
                </RubyPreview>
              ))}
            </RubyPreviewWrapper>
          ) : (
            <PlaceholderText fz={FontSize.SIZE_14}>
              入力するとプレビューが表示されます
            </PlaceholderText>
          )}
        </PreviewSection>

        <Button
          type="button"
          variant={ButtonVariant.PILL}
          color={isSubmitDisabled ? Color.GREY : Color.TINT}
          block
          big
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          追加
        </Button>
      </ContentWrapper>
    </Modal>
  );
};
